import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { authService } from "../services/auth";

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  fallback = <Navigate to="/login" replace />,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Wait for auth service to initialize
        await authService.waitForInitialization();

        // Check if user is authenticated
        const authenticated = authService.isUserAuthenticated();
        setIsAuthenticated(authenticated);

        // If not authenticated, try to refresh token
        if (!authenticated) {
          try {
            const refreshResult = await authService.refreshToken();
            if (refreshResult.success) {
              setIsAuthenticated(true);
            } else {
              setIsAuthenticated(false);
            }
          } catch (error) {
            console.error("Token refresh failed:", error);
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Listen for auth state changes
    const unsubscribe = authService.addUserDataChangeListener(() => {
      const authenticated = authService.isUserAuthenticated();
      setIsAuthenticated(authenticated);
    });

    // Listen for auth failure events from API service
    const handleAuthFailure = () => {
      console.log("Auth failure detected in AuthGuard, redirecting to login");
      setIsAuthenticated(false);
    };

    window.addEventListener("auth-failed", handleAuthFailure);

    return () => {
      unsubscribe();
      window.removeEventListener("auth-failed", handleAuthFailure);
    };
  }, []);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang kiểm tra xác thực...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (isAuthenticated === false) {
    return <>{fallback}</>;
  }

  // If authenticated, render children
  return <>{children}</>;
};

export default AuthGuard;
